import { Link } from 'react-router-dom';
import MetaHead from '../components/MetaHead';
import PhoneCTA from '../components/PhoneCTA';
import EmailCTA from '../components/EmailCTA';
import TestimonialsSlider from '../components/TestimonialsSlider';
import QualitySafetyBox from '../components/QualitySafetyBox';

export default function Home() {
  const services = [
    {
      title: 'Terapi i klinikken',
      subtitle: 'Samtaleterapi, tegne- og kunstterapi',
      description: 'Samtaler og terapeutisk forløb i rolige, trygge rammer i min privatklinik i Gram.',
      location: 'Torvegade 8, 1. sal, 6510 Gram',
      icon: 'fa-regular fa-comments',
      link: '/terapi/samtaleterapi',
      badge: 'I klinikken'
    },
    {
      title: 'Online terapi',
      subtitle: 'Videokonsultation',
      description: 'Fleksibel terapi hjemmefra via en GDPR-sikret og dobbeltkrypteret online platform. Tilgås nemt via sms/e-mail link.',
      location: 'Fra computer eller telefon',
      icon: 'fa-regular fa-laptop-medical',
      link: '/terapi/online-terapi',
      badge: 'Skærm / Mobil'
    },
    {
      title: 'Walk and talk',
      subtitle: 'Terapi under gåtur i naturen',
      description: 'Samtaleterapi i bevægelse i den smukke natur omkring Gram Slotspark. Giver ofte en særlig ro og frisk vinkel.',
      location: 'Mødested ved Gram Slotspark',
      icon: 'fa-regular fa-person-walking',
      link: '/terapi/walk-and-talk',
      badge: 'I naturen'
    }
  ];

  return (
    <div className="bg-white pb-12">
      <MetaHead 
        title="Din Privat Psykolog" 
        description="Privat psykolog, 20 års erfaring, Klinisk psykolog i Syddanmark, Autoriseret psykolog med bred klinisk erfaring, Samtaleterapi med individuelle, samtaleterapi, parterapi, psykologhjælp,  "
        ogTitle="Din Privat Psykolog"
        ogDescription="Privat psykolog, 20 års erfaring, Klinisk psykolog i Syddanmark, Autoriseret psykolog med bred klinisk erfaring, Samtaleterapi med individuelle, samtaleterapi, parterapi, psykologhjælp,  "
        ogImage="https://assets.auxodev.dk/assets/9bc67d49-c1ad-4360-8231-e9811640bce9/pure-forest-creek-stream.jpg"
      />

      {/* Hero Section */}
      <section className="relative overflow-hidden bg-[#5f7365] py-28 sm:py-36 lg:py-44 border-b border-[#EAE2D2] min-h-[520px] lg:min-h-[620px] flex items-center justify-center">
        {/* Full-width atmospheric background video with forest stream */}
        <div className="absolute inset-0 z-0 overflow-hidden">
          <video
            autoPlay
            muted
            loop
            playsInline
            preload="auto"
            poster="/_assets/pure-forest-creek-stream.jpg"
            className="w-full h-full object-cover object-center motion-reduce:hidden"
          >
            <source
              src="/_assets/hero-video-1791275009281.mp4"
              type="video/mp4"
            />
          </video>
          {/* Fallback image for users who prefer reduced motion */}
          <img
            src="/_assets/pure-forest-creek-stream.jpg"
            alt="Rolig skovbæk med trillende vand og skov omkring"
            className="hidden motion-reduce:block absolute inset-0 w-full h-full object-cover object-center"
          />
          {/* Subtle dark gradient overlay for optimal text contrast and readability */}
          <div className="absolute inset-0 bg-gradient-to-r from-[#39473e]/85 via-[#4a5c50]/70 to-[#334237]/65" />
          
          {/* Organiske blide lys-auraer og flydende elementer der skaber levende atmosfære */}
          <div 
            className="absolute -top-12 left-1/6 w-96 h-96 rounded-full bg-white/10 blur-3xl pointer-events-none animate-float-aura" 
            aria-hidden="true" 
          />
          <div 
            className="absolute top-1/3 right-1/6 w-96 h-96 rounded-full bg-[#EFE8DA]/15 blur-3xl pointer-events-none animate-float-reverse" 
            aria-hidden="true" 
          />
          <div 
            className="absolute -bottom-16 left-1/3 w-80 h-80 rounded-full bg-[#D3E0D6]/15 blur-3xl pointer-events-none animate-float-gentle" 
            aria-hidden="true" 
          />
          <div 
            className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[520px] h-[520px] rounded-full bg-white/5 blur-3xl pointer-events-none animate-shimmer-glow" 
            aria-hidden="true" 
          />

          {/* Subtile svævende lysmoter / partikler der driver roligt opad */}
          <div aria-hidden="true" className="pointer-events-none absolute inset-0 overflow-hidden">
            <span className="absolute top-[68%] left-[18%] w-2 h-2 rounded-full bg-white/35 blur-[1px] animate-mote-1" />
            <span className="absolute top-[75%] left-[32%] w-2.5 h-2.5 rounded-full bg-[#EFE8DA]/40 blur-[1px] animate-mote-2" />
            <span className="absolute top-[62%] right-[22%] w-2 h-2 rounded-full bg-white/30 blur-[1px] animate-mote-3" />
            <span className="absolute top-[80%] right-[35%] w-3 h-3 rounded-full bg-[#D3E0D6]/35 blur-[1.5px] animate-mote-4" />
            <span className="absolute top-[50%] left-[45%] w-1.5 h-1.5 rounded-full bg-white/45 blur-[0.5px] animate-mote-2" />
            <span className="absolute top-[58%] right-[12%] w-2.5 h-2.5 rounded-full bg-[#F6F8F6]/35 blur-[1px] animate-mote-1" />
          </div>

          {/* Blide svævende organiske cirkler/bokeh der vugger let i brisen */}
          <div aria-hidden="true" className="pointer-events-none absolute inset-0">
            <div className="absolute top-16 left-[10%] w-24 h-24 rounded-full border border-white/10 animate-gentle-breeze opacity-40" />
            <div className="absolute bottom-20 right-[12%] w-36 h-36 rounded-full border border-white/10 animate-float-reverse opacity-30" />
          </div>
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto text-center space-y-6 sm:space-y-8">
            <div className="space-y-5">
              <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl xl:text-7xl font-bold text-white leading-tight drop-shadow-sm">
                Din Privat Psykolog <br className="hidden sm:inline" />
                Marie Aagaard Larsen
              </h1>
              <p className="text-lg sm:text-xl lg:text-2xl text-white leading-relaxed drop-shadow-2xs max-w-2xl mx-auto opacity-95">
                Nærværende samtaleterapi. Over 20 års klinisk erfaring tilpasset dig og din livssituation.
              </p>
            </div>

            {/* Action Buttons */}
            <div className="flex flex-wrap items-center justify-center gap-4 pt-4 sm:pt-6">
              <Link
                to="/kontakt"
                className="px-6 py-3.5 bg-[#5f7365] hover:bg-[#4e6053] text-white font-semibold rounded-none shadow-sm transition-all duration-200 hover:-translate-y-1 inline-flex items-center gap-2 text-sm focus:outline-none focus:ring-2 focus:ring-white"
              >
                <i className="fa-regular fa-calendar-check" aria-hidden="true" />
                Kontakt og tidsbestilling
              </Link>
              <Link
                to="/terapi/samtaleterapi"
                className="px-6 py-3.5 bg-white/95 hover:bg-white text-[#5f7365] font-semibold rounded-none border border-white/80 transition-all duration-200 hover:-translate-y-1 inline-flex items-center gap-2 text-sm focus:outline-none focus:ring-2 focus:ring-white"
              >
                Læs om terapiformer
                <i className="fa-regular fa-arrow-right" aria-hidden="true" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Prominent 3 Core Services Section: Terapi, Online terapi, Walk and talk */}
      <section className="bg-white py-10 sm:py-14 border-b border-[#EFE8DC]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto space-y-3 mb-12">
            <h2 className="font-serif text-2xl sm:text-3xl lg:text-4xl font-bold text-[#5f7365]">
              Tre tilpassede indgange til dit samtaleforløb
            </h2>
            <p className="text-sm sm:text-base text-[#465A4F] leading-relaxed">
              Hvad enten du foretrækker et klassisk møde i klinikken, fleksibiliteten ved en online videokonsultation eller samtaleterapi i bevægelse på en walk and talk i skoven, finder vi sammen den rette ramme.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {services.map((service) => (
              <Link
                key={service.title}
                to={service.link}
                className="group bg-white rounded-none p-7 flex flex-col justify-between shadow-md hover:shadow-xl transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-[#5f7365]"
              >
                <div className="space-y-4">
                  <div>
                    <i className={`${service.icon} text-3xl text-[#5f7365]`} aria-hidden="true" />
                  </div>

                  <div className="space-y-1.5">
                    <h3 className="font-serif text-xl font-bold text-[#5f7365] group-hover:text-[#4e6053] transition-colors">
                      {service.title}
                    </h3>
                    <p className="text-xs font-semibold text-[#5A7364]">
                      {service.subtitle}
                    </p>
                    <p className="text-sm text-[#465A4F] leading-relaxed pt-1">
                      {service.description}
                    </p>
                  </div>
                </div>

                <div className="pt-6 mt-6 border-t border-[#EAE3D5] flex items-center justify-between text-xs font-medium text-[#5f7365]">
                  <span className="flex items-center gap-1.5 text-[#5A7364]">
                    <i className="fa-regular fa-location-dot" />
                    {service.location}
                  </span>
                  <span className="inline-flex items-center gap-1 font-semibold group-hover:translate-x-1 transition-transform">
                    Læs mere
                    <i className="fa-regular fa-arrow-right text-[11px]" />
                  </span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Om mig Section directly under 3 Services */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-12 sm:pt-16 pb-4 sm:pb-6">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
          
          <div className="lg:col-span-7 space-y-5">
            <h2 className="font-serif text-2xl sm:text-3xl lg:text-4xl font-bold text-[#5f7365]">
              Om Marie Aagaard Larsen
            </h2>
            <p className="text-base sm:text-lg text-[#5f7365] font-medium leading-relaxed">
              Klinisk psykolog cand. psych. aut. med over 20 års bred erfaring inden for behandlingsområdet, krisehjælp og supervision.
            </p>
            <p className="text-sm sm:text-base text-[#465A4F] leading-relaxed">
              Jeg er autoriseret klinisk psykolog og er underlagt Psykologloven. Jeg tilrettelægger og tilpasser altid metode, form og indhold efter hver enkelt klients specifikke situation, behov og ressourcer. Min tilgang favner det hele menneske – dine følelser, tanker og kropslige oplevelser.
            </p>

            <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2.5 pt-1 text-xs sm:text-sm text-[#5f7365]">
              <li className="flex items-start gap-2">
                <i className="fa-regular fa-check text-[#5f7365] font-bold mt-0.5" />
                <span>Autoriseret af Psykolognævnet (2011)</span>
              </li>
              <li className="flex items-start gap-2">
                <i className="fa-regular fa-check text-[#5f7365] font-bold mt-0.5" />
                <span>Kandidat i psykologi (cand. psych. aut.)</span>
              </li>
              <li className="flex items-start gap-2">
                <i className="fa-regular fa-check text-[#5f7365] font-bold mt-0.5" />
                <span>Efteruddannet i EMDR, mentalisering og ACT</span>
              </li>
              <li className="flex items-start gap-2">
                <i className="fa-regular fa-check text-[#5f7365] font-bold mt-0.5" />
                <span>Særlig ekspertise i kriseterapi og traumer</span>
              </li>
            </ul>

            <div className="flex flex-wrap items-center gap-4 pt-3">
              <Link
                to="/om"
                className="px-5 py-3 bg-[#5f7365] hover:bg-[#4e6053] text-white font-semibold rounded-none text-xs sm:text-sm transition-all duration-200 hover:-translate-y-1 inline-flex items-center gap-2"
              >
                <span>Læs hele min profil</span>
                <i className="fa-regular fa-arrow-right text-xs" />
              </Link>
              <Link
                to="/cv"
                className="px-5 py-3 bg-white hover:bg-white text-[#5f7365] font-semibold rounded-none border border-[#5f7365] text-xs sm:text-sm transition-all duration-200 hover:-translate-y-1 inline-flex items-center gap-2"
              >
                <i className="fa-regular fa-file-lines text-xs text-[#5f7365]" />
                <span>Se mit CV</span>
              </Link>
            </div>
          </div>

          <div className="lg:col-span-5 relative">
            <div className="overflow-hidden shadow-sm">
              <img
                src="https://assets.auxodev.dk/cdn-cgi/image/width=800,quality=85,format=auto/assets/9bc67d49-c1ad-4360-8231-e9811640bce9/5780272f-d500-4281-a885-5a0370f36520.jpg"
                alt="Marie Aagaard Larsen - Autoriseret Psykolog i Gram"
                className="w-full h-auto object-cover"
              />
            </div>
          </div>

        </div>

        {/* Integrated HumanEye Feature Box inside Om section */}
        <div className="mt-10 p-6 sm:p-7 bg-[#FAF8F5] border border-[#E3D9CC] border-l-4 border-l-[#5f7365] flex flex-col md:flex-row items-start md:items-center justify-between gap-5">
          <div className="space-y-1.5 max-w-2xl">
            <div className="inline-flex items-center gap-2 text-xs uppercase tracking-wider text-[#5f7365] font-bold">
              <span>Erhvervspsykolog, supervision og ledelsessparring</span>
            </div>
            <h3 className="font-serif text-lg sm:text-xl font-bold text-[#5f7365]">
              Marie Aagaard Larsen driver også humaneye.dk
            </h3>
            <p className="text-xs sm:text-sm text-[#465A4F] leading-relaxed">
              Ønsker du erhvervspsykologiske ydelser, ledelsescoaching, medarbejderudvikling eller supervision af faggrupper? Læs mere på HumanEye.
            </p>
          </div>

          <a
            href="http://www.humaneye.dk/"
            target="_blank"
            rel="noopener noreferrer"
            className="px-5 py-3 bg-[#5f7365] hover:bg-[#4e6053] text-white font-semibold rounded-none text-xs sm:text-sm transition-all duration-200 hover:-translate-y-1 inline-flex items-center gap-2 whitespace-nowrap shrink-0"
          >
            <span>Besøg humaneye.dk (Erhvervspsykolog)</span>
            <i className="fa-regular fa-arrow-up-right-from-square text-xs" />
          </a>
        </div>
      </section>

      {/* Testimonials Slider */}
      <TestimonialsSlider />

      {/* Kvalitet, sikkerhed og ansvar */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12">
        <QualitySafetyBox />
      </section>

      {/* Primary CTA Section: Phone & Email CTAs */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-12 sm:mt-16">
        <div className="bg-white border border-[#EDE6D8] p-7 sm:p-10 lg:p-12 relative overflow-hidden">
          {/* Subtil minimalistisk baggrundsdetalje */}
          <div className="absolute top-0 right-0 w-72 h-72 bg-[#FAF8F5] rounded-full blur-3xl -mr-24 -mt-24 pointer-events-none opacity-70" aria-hidden="true" />

          <div className="relative z-10 grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-center">
            {/* Tekst og info */}
            <div className="lg:col-span-5 space-y-4 sm:space-y-5">
              <div className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-[#5f7365]">
                <span className="w-1.5 h-1.5 rounded-full bg-[#5f7365]" />
                Tidsbestilling og henvendelser
              </div>
              <h2 className="font-serif text-2xl sm:text-3xl lg:text-4xl text-[#2C3B32] font-normal tracking-tight">
                Kontakt privatklinikken
              </h2>
              <p className="text-sm sm:text-base text-[#55695E] leading-relaxed">
                Du er meget velkommen til at kontakte mig, så du kan mærke efter, om jeg skal være din privatpsykolog. Aktuelt er der mellem 3-6 ugers ventetid med mulighed for at komme på en afbudsliste.
              </p>

              <div className="pt-1">
                <Link
                  to="/kontakt"
                  className="inline-flex items-center gap-2 text-xs sm:text-sm font-medium text-[#5f7365] hover:text-[#2C3B32] transition-colors group"
                >
                  <span>Gå til kontaktformular og rutevejledning</span>
                  <i className="fa-regular fa-arrow-right text-xs transition-transform duration-200 group-hover:translate-x-1" aria-hidden="true" />
                </Link>
              </div>
            </div>

            {/* Kontaktkort */}
            <div className="lg:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-5">
              {/* Telefonkort */}
              <div className="bg-white border border-[#5f7365] p-6 flex flex-col justify-between hover:border-[#2C3B32] transition-colors group">
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-[11px] font-semibold uppercase tracking-wider text-[#7B8E81]">Telefon</span>
                    <div className="w-8 h-8 rounded-full bg-white border border-[#EDE6D8] flex items-center justify-center text-[#5f7365] text-xs group-hover:bg-[#5f7365] group-hover:text-white group-hover:border-[#5f7365] transition-colors">
                      <i className="fa-regular fa-phone" aria-hidden="true" />
                    </div>
                  </div>
                  <div>
                    <a
                      href="tel:29921879"
                      className="font-mono text-xl sm:text-2xl font-bold text-[#2C3B32] hover:text-[#5f7365] transition-colors inline-block focus:outline-none focus:underline"
                      aria-label="Ring til 29 92 18 79"
                    >
                      29 92 18 79
                    </a>
                    <div className="flex items-start gap-1.5 text-xs text-[#55695E] pt-2">
                      <i className="fa-regular fa-clock text-[#7B8E81] text-[11px] mt-0.5 shrink-0" aria-hidden="true" />
                      <span>Telefontid: 12.45 - 13.00 på hverdage (undtagen onsdage)</span>
                    </div>
                  </div>
                </div>
                <div className="pt-4 mt-4 border-t border-[#EDE6D8]/60">
                  <a
                    href="tel:29921879"
                    className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#5f7365] hover:text-[#2C3B32] transition-colors"
                  >
                    <span>Ring op nu</span>
                    <i className="fa-regular fa-arrow-up-right text-[10px]" aria-hidden="true" />
                  </a>
                </div>
              </div>

              {/* E-mailkort */}
              <div className="bg-white border border-[#5f7365] p-6 flex flex-col justify-between hover:border-[#2C3B32] transition-colors group">
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-[11px] font-semibold uppercase tracking-wider text-[#7B8E81]">E-mail</span>
                    <div className="w-8 h-8 rounded-full bg-white border border-[#EDE6D8] flex items-center justify-center text-[#5f7365] text-xs group-hover:bg-[#5f7365] group-hover:text-white group-hover:border-[#5f7365] transition-colors">
                      <i className="fa-regular fa-envelope" aria-hidden="true" />
                    </div>
                  </div>
                  <div>
                    <a
                      href="mailto:kontakt@dinprivatpsykolog.dk"
                      className="text-sm sm:text-base font-semibold text-[#2C3B32] hover:text-[#5f7365] transition-colors break-all inline-block focus:outline-none focus:underline"
                      aria-label="Send e-mail til kontakt@dinprivatpsykolog.dk"
                    >
                      kontakt@dinprivatpsykolog.dk
                    </a>
                    <p className="text-xs text-[#55695E] pt-2 leading-relaxed">
                      Send gerne en besked om din henvendelse, så vender jeg hurtigt tilbage.
                    </p>
                  </div>
                </div>
                <div className="pt-4 mt-4 border-t border-[#EDE6D8]/60">
                  <a
                    href="mailto:kontakt@dinprivatpsykolog.dk"
                    className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#5f7365] hover:text-[#2C3B32] transition-colors"
                  >
                    <span>Send e-mail</span>
                    <i className="fa-regular fa-arrow-up-right text-[10px]" aria-hidden="true" />
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
